import { useState, useEffect, useCallback, useRef } from 'react';
import { getHostName } from '../tools/office-tools-registry';
import { findOrCreateWorkspace } from '../api/workspaces';
import type { Workspace } from '../types/workspace.types';

/**
 * Resolves (or creates) a workspace named after the current Office host.
 * E.g. if the add-in is open in PowerPoint, the workspace will be "PowerPoint".
 */
export function useWorkspace() {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const resolvingRef = useRef(false);

  const resolve = useCallback(async () => {
    // Prevent concurrent calls (React Strict Mode fires effects twice)
    if (resolvingRef.current) return;
    resolvingRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const hostName = getHostName();
      const ws = await findOrCreateWorkspace(
        hostName,
        `Workspace for Office ${hostName} add-in`
      );
      setWorkspace(ws);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to resolve workspace';
      setError(message);
      console.error('[useWorkspace] Failed to resolve workspace:', err);
    } finally {
      setLoading(false);
      resolvingRef.current = false;
    }
  }, []);

  useEffect(() => {
    resolve();
  }, [resolve]);

  return { workspace, loading, error, refresh: resolve };
}
